const STANDBY_HOSTNAMES = new Set([
  "aipm.linfinityeng.com",
  "polite-pebble-0bf8e4700.6.azurestaticapps.net",
]);

export const STANDBY_REAUTH_KEY = "pmapp-standby-swa-reauth";

type LocationLike = {
  hostname: string;
  pathname: string;
  search: string;
  hash: string;
};

type TryStandbyReauthOptions = {
  location?: LocationLike;
  sessionStorage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  redirect?: (url: string) => void;
};

function getLocationLike(locationOverride?: LocationLike): LocationLike | null {
  if (locationOverride) {
    return locationOverride;
  }

  if (typeof window === "undefined") {
    return null;
  }

  return window.location;
}

export function isStandbyHostname(hostname?: string | null): boolean {
  const resolvedHostname = String(
    hostname ??
    (typeof window !== "undefined" ? window.location.hostname : ""),
  )
    .trim()
    .toLowerCase();

  return STANDBY_HOSTNAMES.has(resolvedHostname);
}

export function getCurrentReturnTo(locationOverride?: LocationLike): string {
  const location = getLocationLike(locationOverride);
  if (!location) {
    return "/";
  }

  return `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`;
}

export function clearStandbyReauthAttempt(sessionStorageOverride?: Pick<Storage, "removeItem">) {
  if (sessionStorageOverride) {
    sessionStorageOverride.removeItem(STANDBY_REAUTH_KEY);
    return;
  }

  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(STANDBY_REAUTH_KEY);
}

export function tryStandbyReauth(returnTo: string, options: TryStandbyReauthOptions = {}): boolean {
  const location = getLocationLike(options.location);
  if (!location || !isStandbyHostname(location.hostname)) {
    return false;
  }

  const storage = options.sessionStorage ??
    (typeof window !== "undefined" ? window.sessionStorage : null);
  if (!storage) {
    return false;
  }

  const redirect = options.redirect ??
    ((url: string) => {
      if (typeof window !== "undefined") {
        window.location.href = url;
      }
    });

  try {
    const attemptedAt = storage.getItem(STANDBY_REAUTH_KEY);
    if (attemptedAt) {
      return false;
    }

    storage.setItem(STANDBY_REAUTH_KEY, new Date().toISOString());
    const loginPath = "/.auth/login/aad?post_login_redirect_uri=" + encodeURIComponent(returnTo);
    const logoutPath = "/.auth/logout?post_logout_redirect_uri=" + encodeURIComponent(loginPath);
    console.warn("⚠️ SWA Auth: Standby session looked stale; forcing one clean re-auth");
    redirect(logoutPath);
    return true;
  } catch (error) {
    console.warn("⚠️ SWA Auth: Failed to trigger standby re-auth", error);
    return false;
  }
}
